import { formatCurrency } from '@erp/shared-utils';

/**
 * Whole dollars by default ('$4,998'); pass `{ cents: true }` for unit prices
 * and invoice lines ('$424.36'). Negatives use the shared accounting style.
 */
export function fmtMoney(n: number, opts?: { cents?: boolean }): string {
  if (opts?.cents) return formatCurrency(n);
  return formatCurrency(Math.round(n)).replace(/\.00(\)?)$/, '$1');
}

function trimTrailingZero(s: string): string {
  return s.replace(/\.0$/, '');
}

/** '$2.14B', '$48.2M', '$412K', '$850'. */
export function fmtMoneyCompact(n: number): string {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e8) return `${sign}$${Math.round(abs / 1e6)}M`;
  if (abs >= 1e6) return `${sign}$${trimTrailingZero((abs / 1e6).toFixed(1))}M`;
  if (abs >= 1e3) return `${sign}$${Math.round(abs / 1e3)}K`;
  return `${sign}$${Math.round(abs)}`;
}

/** Input is already a percent value: `fmtPct(91)` is '91.0%'. */
export function fmtPct(n: number, digits = 1): string {
  return `${n.toFixed(digits)}%`;
}

const NUMBER_FORMATTER = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});

/** '142,000', '4.8'. */
export function fmtNumber(n: number): string {
  return NUMBER_FORMATTER.format(n);
}

/*
 * Units. The data keeps wire in C (hundreds of feet), the unit wire is bought
 * and priced in. The UI never shows "C": quantities read in feet and prices
 * per 100 ft. Everything else keeps its own unit.
 */
const FEET_PER_C = 100;

/** Quantity as shown: feet for wire (700 C is 70,000), otherwise unchanged. */
export function displayQty(qty: number, unit: string): number {
  return unit === 'C' ? qty * FEET_PER_C : qty;
}

/** Unit shown next to a quantity: 'ft' for wire, 'length' for conduit sticks. */
export function displayUnit(unit: string): string {
  if (unit === 'C') return 'ft';
  if (unit === 'stick') return 'length';
  return unit;
}

/** '70,000 ft', '40 each', '120 fixture'. Pluralize at the call site if needed. */
export function fmtQtyWithUnit(qty: number, unit: string): string {
  return `${fmtNumber(displayQty(qty, unit))} ${displayUnit(unit)}`;
}

/** What a unit price is per: '100 ft' for wire, '10 ft length' for conduit. */
export function priceUnit(unit: string): string {
  if (unit === 'C') return '100 ft';
  if (unit === 'stick') return '10 ft length';
  return unit;
}

/** '$424.36 per 100 ft', '$5,500 each', '$88.70 per fixture'. */
export function fmtPricePer(price: number, unit: string): string {
  const money = fmtMoney(price, { cents: !Number.isInteger(price) });
  return unit === 'each' ? `${money} each` : `${money} per ${priceUnit(unit)}`;
}
